import React, { useEffect, useRef, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import { prop, slice, sortBy } from "ramda";

import Paper from "@mui/material/Paper";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";

import { mapWithIndex, notBlank } from "../../util/ext";
import { Box } from "@mui/material";
import { DateTime } from "luxon";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import ContentIngestionErrorsDialog from "./ContentIngestionErrorsDialog";
import CustomTablePagination from "../../creator-application/common/CustomTablePagination";

const DEFAULT_ROWS_PER_PAGE = 20;

const useStyles = makeStyles(theme => ({
  root: {
    width: "calc(100% + 12px)",
    marginLeft: "-6px",
    borderRadius: "20px",
  },

  link: {
    color: theme.palette.primary.brandPrimary,
  },

  tableContainer: {
    height: "calc(100vh - 550px)",
    overflow: "auto",
  },

  pagination: {
    color: theme.palette.primary.darker,
    backgroundColor: "white",
    borderRadius: "20px",
  },
}));

const ChunkStepsTable = props => {
  const { steps, page, onPageChange, onScrollVisible } = props;

  const classes = useStyles();
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);

  //-- Track if the scroll bar is visible
  const tableContainerEl = useRef(null);
  useEffect(() => {
    if (notBlank(tableContainerEl)) {
      const element = tableContainerEl.current;
      const scrollVisible = element.scrollHeight > element.clientHeight;
      onScrollVisible(scrollVisible);
    }
  }, [onScrollVisible]);

  const [errorReportDialogOpen, setErrorReportDialogOpen] = React.useState(
    false,
  );
  const [errorReportLocation, setErrorReportLocation] = React.useState(null);
  const openErrorReport = location => {
    setErrorReportLocation(location);
    setErrorReportDialogOpen(true);
  };

  const handleRowsPerPageChange = event => {
    setRowsPerPage(event.target.value);
  };

  return (
    <Paper elevation={0} className={classes.root}>
      {notBlank(errorReportLocation) && errorReportDialogOpen && (
        <ContentIngestionErrorsDialog
          errorFileLocation={errorReportLocation}
          open={errorReportDialogOpen}
          onClose={() => setErrorReportDialogOpen(false)}
        />
      )}
      <TableContainer className={classes.tableContainer} ref={tableContainerEl}>
        <Table stickyHeader aria-label="Steps">
          <TableHead>
            <TableRow>
              <TableCell
                key="timestamp"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Timestamp
              </TableCell>

              <TableCell
                key="event"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Event
              </TableCell>

              <TableCell
                key="task-name"
                align="left"
                style={{ minWidth: 75, maxWidth: 600 }}
              >
                Task Name
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {mapWithIndex(
              (step, idx) => (
                <TableRow hover tabIndex={-1} key={`step-${idx}`}>
                  <TableCell
                    key={"timestamp:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {DateTime.fromISO(step.eventTimestamp)
                      .toUTC()
                      .toLocaleString(DateTime.DATETIME_SHORT_WITH_SECONDS)}
                  </TableCell>

                  <TableCell
                    key={"event:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    <Box display="flex" alignSelf="center">
                      <Box>{step.eventName}</Box>
                      {notBlank(step.errorDetailsLocation) && (
                        <Box mt={-2.5}>
                          <Tooltip title="View Error Report">
                            <IconButton
                              onClick={() =>
                                openErrorReport(step.errorDetailsLocation)
                              }
                              size="large"
                            >
                              <ErrorOutlineIcon
                                color="error"
                                fontSize="large"
                              />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </Box>
                  </TableCell>

                  <TableCell
                    key={"task-name:" + idx}
                    align="left"
                    style={{ minWidth: 75, maxWidth: 600 }}
                  >
                    {step.taskName}
                  </TableCell>
                </TableRow>
              ),
              slice(
                page * rowsPerPage,
                page * rowsPerPage + rowsPerPage,
                sortBy(prop("eventTimestamp"), steps),
              ),
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <CustomTablePagination
        classes={{
          root: classes.pagination,
        }}
        colSpan={4}
        component="div"
        count={steps.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </Paper>
  );
};

export default ChunkStepsTable;
